/**
 * WifiConfigModal — DEVICE-DETAIL-MANAGE-1E.
 *
 * Reconfigura o Wi-Fi do device a partir da página Detalhes, usando o endpoint JÁ
 * existente e guardado:
 *   POST /api/admin/devices/{id}/config/wifi  (configWifi em api.js)
 *
 * Backend = fonte da verdade dos guards: exige CAP_DEVICES_CONFIG (botão gated por
 * CAP.DEVICES_CONFIG) + confirmação forte SEMPRE (S3.10C1). NÃO cria comando fora
 * deste endpoint; NÃO toca HMAC/MAC/firmware/telemetria.
 *
 * IMPORTANTE (contrato real): o endpoint SUBSTITUI a config de Wi-Fi e envia o
 * `password` exatamente como recebido — `null` = REDE ABERTA. Não existe "manter a
 * senha atual": deixar em branco limparia a senha. Por isso a senha NUNCA é
 * pré-preenchida (não vem do backend) e é OBRIGATÓRIA, salvo "rede aberta".
 *
 * Copy honesta: a config é enviada como PENDENTE e aplicada no próximo heartbeat/ACK
 * do device — não é reconexão imediata.
 */
import React, { useEffect, useState } from "react";

import { configWifi, getDeviceConfigSnapshot } from "../lib/api.js";
import { useCriticalConfirm } from "./ConfirmCriticalAction.jsx";

const INPUT =
  "w-full bg-noc-bg border border-noc-border rounded-md px-2 py-1.5 " +
  "text-[13px] text-noc-text focus:border-noc-accent outline-none disabled:opacity-40";

function friendlyErr(e) {
  const s = e?.status;
  if (s === 401) return "Sessão expirada — faça login novamente.";
  if (s === 403) return "Sem permissão (devices:config) ou confirmação recusada.";
  if (s === 404) return "Device não encontrado.";
  if (s === 422) return `Dado inválido: ${e?.message || "verifique os campos."}`;
  return e?.message || "Falha ao reconfigurar o Wi-Fi.";
}

export default function WifiConfigModal({ deviceId, device, onClose, onSaved }) {
  const confirmCritical = useCriticalConfirm();
  const protectedDev = !!device?.ota_protected;

  const [ssid, setSsid] = useState("");
  const [password, setPassword] = useState("");
  const [openNet, setOpenNet] = useState(false);  // rede aberta (sem senha)
  const [hidden, setHidden] = useState(false);
  const [showPw, setShowPw] = useState(false);

  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  // Prefill do SSID/hidden a partir do último snapshot de config do device.
  // A SENHA nunca é pré-preenchida (não vem do backend, por segurança).
  useEffect(() => {
    let alive = true;
    getDeviceConfigSnapshot(deviceId)
      .then((r) => {
        if (!alive) return;
        const w = r?.last_config_snapshot?.wifi || {};
        if (w.ssid) setSsid(String(w.ssid));
        if (typeof w.hidden === "boolean") setHidden(w.hidden);
      })
      .catch(() => {});
    return () => { alive = false; };
  }, [deviceId]);

  function validate() {
    const s = ssid.trim();
    if (s.length < 1 || s.length > 32) return "SSID deve ter de 1 a 32 caracteres.";
    if (!openNet) {
      if (password.length < 1) return "Informe a senha (ou marque 'Rede aberta').";
      if (password.length > 64) return "Senha deve ter no máximo 64 caracteres.";
    }
    return null;
  }

  async function handleSave() {
    const v = validate();
    if (v) { setError(v); return; }
    setError(null);
    const secret = await confirmCritical({
      actionLabel: "Reconfigurar Wi-Fi",
      target: { kind: "single", deviceId },
      impact: protectedDev
        ? "Device PROTEGIDO. Mudar SSID/senha pode DESCONECTAR o device em campo."
        : "Mudar SSID/senha pode DESCONECTAR o device em campo (aplica no próximo heartbeat).",
    });
    if (secret == null) return;
    setSaving(true);
    try {
      const body = {
        ssid: ssid.trim(),
        password: openNet ? null : password,
        hidden,
      };
      await configWifi(deviceId, body, { confirm: secret });
      onSaved && onSaved();
    } catch (e) {
      setError(friendlyErr(e));
      setSaving(false);
    }
  }

  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4"
      onClick={saving ? undefined : onClose}>
      <div className="bg-noc-panel border border-noc-border rounded-xl w-full max-w-lg
                      max-h-[90vh] overflow-y-auto p-5 space-y-4"
        onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between">
          <h2 className="text-[15px] font-semibold text-noc-text">Reconfigurar Wi-Fi</h2>
          <span className="font-mono text-[11px] text-noc-faint">{deviceId}</span>
        </div>

        <div className="text-[12px] bg-noc-warn/10 border border-noc-warn/30 text-noc-warn
                        rounded-md px-3 py-2">
          ⚠ Isto <b>substitui</b> a configuração de Wi-Fi (SSID + senha). Mudar a rede pode
          <b> desconectar o device</b>; a nova config é enviada como <b>pendente</b> e aplicada
          no próximo heartbeat (não é imediato). A senha não é pré-preenchida por segurança —
          informe as credenciais completas da nova rede.
          {protectedDev && " 🔒 Device PROTEGIDO (produção/gold/canário)."}
        </div>

        {error && (
          <div className="text-[12px] bg-noc-err/10 border border-noc-err/30 text-noc-err
                          rounded-md px-3 py-2">{error}</div>
        )}

        <div className="space-y-1">
          <label className="text-[11px] uppercase tracking-wide text-noc-faint">SSID (1–32)</label>
          <input className={INPUT} maxLength={32} placeholder="Nome da rede"
            value={ssid} onChange={(e) => setSsid(e.target.value)} />
        </div>

        <div className="space-y-1">
          <label className="text-[11px] uppercase tracking-wide text-noc-faint">
            Senha {openNet ? "(rede aberta — não usada)" : "(1–64)"}
          </label>
          <div className="flex gap-2">
            <input className={INPUT} type={showPw ? "text" : "password"} maxLength={64}
              placeholder={openNet ? "—" : "Senha da nova rede"} value={password}
              disabled={openNet} autoComplete="new-password"
              onChange={(e) => setPassword(e.target.value)} />
            <button type="button" className="btn-ghost text-[11px]"
              onClick={() => setShowPw((v) => !v)} disabled={openNet}>
              {showPw ? "Ocultar" : "Mostrar"}
            </button>
          </div>
        </div>

        <label className="flex items-center gap-2 text-[13px] text-noc-text">
          <input type="checkbox" checked={openNet} onChange={(e) => setOpenNet(e.target.checked)} />
          Rede aberta (sem senha)
        </label>
        <label className="flex items-center gap-2 text-[13px] text-noc-text">
          <input type="checkbox" checked={hidden} onChange={(e) => setHidden(e.target.checked)} />
          SSID oculto
        </label>

        <div className="flex items-center justify-end gap-2 pt-1">
          <button className="btn-ghost" onClick={onClose} disabled={saving}>Cancelar</button>
          <button className="btn-primary" onClick={handleSave} disabled={saving}>
            {saving ? "Enviando…" : "Enviar ao device"}
          </button>
        </div>
      </div>
    </div>
  );
}
